/**
 * This panel is shown for artifacts of type 'lora'.
 */
import {Banner} from '@wandb/components';
import {CodeEditor} from '@wandb/weave/components/CodeEditor';
import {Loading} from '@wandb/weave/components/Loading';
// While this generates an error, the proposed replacement seems worse to me.
// eslint-disable-next-line wandb/no-deprecated-imports
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import {OutputNode} from '@wandb/weave/core';
import {opArtifactVersionMetadata} from '@wandb/weave/core/ops/domain/artifactVersion';
import {useNodeValue} from '@wandb/weave/react';
import React, {useMemo, useState} from 'react';

import CopyableText from '../../../../components/CopyableText';
import {OverviewItem} from './OverviewItem';
import * as S from './style';

// TODO: Unify with ModelDetailsLoaded.tsx

const BASE_URL_QA = 'https://api.qa.inference.wandb.ai/v1';
const BASE_URL_PROD = 'https://api.inference.wandb.ai/v1';
const BASE_URL =
  window.location.hostname === 'qa.wandb.ai' ? BASE_URL_QA : BASE_URL_PROD;

const CODE_EXAMPLES_CHAT: Record<string, string> = {
  Python: `
import openai
import weave

# Weave autopatches OpenAI to log LLM calls to W&B
weave.init("{entity}/{project}")

client = openai.OpenAI(
    # The custom base URL points to W&B Inference
    base_url='${BASE_URL}',

    # Get your API key from ${window.location.origin}/authorize
    # Consider setting it in the environment as OPENAI_API_KEY instead for safety
    api_key="<your-apikey>",

    # Optional: Team and project for usage tracking
    project="{entity}/{project}",
)

response = client.chat.completions.create(
    model="{model_id}",
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "Tell me a joke."}
    ],
)

print(response.choices[0].message.content)`,
  Curl: `
curl ${BASE_URL}/chat/completions \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer <your-apikey>" \\
-H "OpenAI-Project: {entity}/{project}" \\
-d '{
    "model": "{model_id}",
    "messages": [
    { "role": "system", "content": "You are a helpful assistant." },
    { "role": "user", "content": "Tell me a joke." }
    ]
}'
    `,
};

const CODE_LANGUAGE_MAP: Record<string, string> = {
  Python: 'python',
  Curl: 'shell',
};

// Type matches the result from getDataDict in index.tsx
type ArtifactMembershipResult = {
  entityName: string;
  projectName: string;
  artifactName: string;
  versionIndex: number;
  storageRegion: string;
};

type InferenceAccessPanelProps = {
  result: ArtifactMembershipResult;
  artifactVersionNode: OutputNode<'artifactVersion'>;
};

export const InferenceAccessPanel: React.FC<InferenceAccessPanelProps> = ({
  result,
  artifactVersionNode,
}) => {
  const {loading, result: metadataResult} = useNodeValue(
    useMemo(() => {
      return opArtifactVersionMetadata({
        artifactVersion: artifactVersionNode,
      });
    }, [artifactVersionNode])
  );

  const [selectedLanguage, setSelectedLanguage] = useState('Python');

  if (loading) {
    return <Loading />;
  }

  let body = null;
  if (result.storageRegion !== 'coreweave-us') {
    body = (
      <Banner variant="warning" isDismissible={false}>
        LoRA inference is only supported for artifacts stored in the{' '}
        <span className="font-mono text-sm">coreweave-us</span> region.
      </Banner>
    );
  } else {
    const baseModel = metadataResult?.['wandb.base_model'];
    const artifactUri = `wandb-artifact:///${result.entityName}/${result.projectName}/${result.artifactName}:v${result.versionIndex}`;
    const modelId = baseModel ? artifactUri : `<base_model_id>|${artifactUri}`;

    let codeExample = CODE_EXAMPLES_CHAT[selectedLanguage] ?? '';
    codeExample = codeExample.trim();
    codeExample = codeExample.replaceAll('{entity}', result.entityName);
    codeExample = codeExample.replaceAll('{project}', result.projectName);
    codeExample = codeExample.replaceAll('{model_id}', modelId);

    body = (
      <>
        <S.OverviewPanelSectionBodyRow>
          <S.OverviewPanelSectionBodyColumn>
            <OverviewItem
              label="Model URI"
              value={
                <CopyableText
                  text={artifactUri}
                  toastText={'Copied model URI to clipboard'}
                />
              }
            />
          </S.OverviewPanelSectionBodyColumn>
        </S.OverviewPanelSectionBodyRow>
        <S.OverviewPanelSectionBodyRow>
          <S.OverviewPanelSectionBodyColumn>
            <OverviewItem
              label="Use this model"
              value={
                <>
                  <div className="mb-8 flex items-center">
                    <div className="flex-grow">
                      <ToggleButtonGroup
                        options={[{value: 'Python'}, {value: 'Curl'}]}
                        value={selectedLanguage}
                        size="small"
                        onValueChange={setSelectedLanguage}
                      />
                    </div>
                  </div>
                  <div className="[&_.monaco-editor]:!absolute">
                    <CodeEditor
                      value={codeExample}
                      language={CODE_LANGUAGE_MAP[selectedLanguage]}
                      readOnly
                      handleMouseWheel
                      alwaysConsumeMouseWheel={false}
                    />
                  </div>
                </>
              }
            />
          </S.OverviewPanelSectionBodyColumn>
        </S.OverviewPanelSectionBodyRow>
      </>
    );
  }

  return (
    <S.OverviewPanelSection>
      <S.OverviewPanelSectionHeader>
        <S.OverviewPanelSectionHeaderTitle>
          Inference access
        </S.OverviewPanelSectionHeaderTitle>
      </S.OverviewPanelSectionHeader>
      <S.OverviewPanelSectionBody>{body}</S.OverviewPanelSectionBody>
    </S.OverviewPanelSection>
  );
};
